import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronUp, Wrench } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { EASE_OUT } from '@/lib/ease';
import tinycolor from 'tinycolor2';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

import { useCustomerTheme } from '../Theme';

export type CustomerToolOption = {
    id: string;
    name: string;
    description?: string;
};

interface ToolSelectorProps {
    tools: CustomerToolOption[];
    selectedToolId: string;
    onToolChange: (toolId: string) => void;
    disabled?: boolean;
    loading?: boolean;
}

export const ToolSelector: React.FC<ToolSelectorProps> = ({
    tools,
    selectedToolId,
    onToolChange,
    disabled = false,
    loading = false,
}) => {
    const { t } = useTranslation();
    const { theme } = useCustomerTheme();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    /**
     * Auswahlfläche in der Kundenfarbe (Sekundärton, 20 % Deckkraft) — dynamisch
     * aus dem Kundentheme berechnet, deshalb ein Inline-Style. Ohne Kundenfarbe
     * trägt die Zeile den `accent`-Token wie überall sonst im Portal.
     */
    const selectedTint = theme?.secondary
        ? tinycolor(theme.secondary).setAlpha(0.2).toRgbString()
        : undefined;

    const rowStyle = (selected: boolean) =>
        selected && selectedTint ? { backgroundColor: selectedTint } : undefined;

    const rowClass = (selected: boolean) =>
        cn(
            'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent',
            selected && !selectedTint && 'bg-accent',
        );

    const selectedTool = tools.find((tool) => tool.id === selectedToolId);

    const getDisplayText = () => {
        if (loading) {
            return t('chatExchange.toolSelection.loading');
        }
        if (!selectedToolId || !selectedTool) {
            return t('chatExchange.toolSelection.none');
        }
        return selectedTool.name;
    };

    const isDisabled = disabled || loading;
    const reduceMotion = useReducedMotion() ?? false;

    return (
        <div ref={dropdownRef} className="relative">
            {/* In der Aktionszeile des Chats steht die Wahl als Nebengeräusch:
                ohne Rahmen und ohne Fläche, aus der Opazitätsleiter, mit Fläche
                erst beim Überfahren. */}
            <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="chat-tool-picker"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => !isDisabled && setIsOpen(!isOpen)}
                disabled={isDisabled}
                className="h-8 w-fit min-w-0 max-w-[280px] justify-between gap-1.5 overflow-hidden rounded-full px-2.5 text-sm font-normal text-[var(--oa-fg-3)] hover:bg-[var(--oa-surface)] hover:text-[var(--oa-fg)]"
            >
                {/* A tool glyph stands in while nothing is picked, so the control reads as the
                    tool picker and not as a stray label (28.09.2026). */}
                {!selectedToolId && <Wrench className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                <span className="min-w-0 flex-1 truncate" title={selectedTool?.name}>
                    {getDisplayText()}
                </span>
                {isOpen
                    ? <ChevronUp className="h-2.5 w-2.5 shrink-0" aria-hidden />
                    : <ChevronDown className="h-2.5 w-2.5 shrink-0" aria-hidden />}
            </Button>

            {/* The menu unfolds from the control: a short fade with a slight rise and scale,
                mirrored on close; `motion-reduce` cuts. */}
            <AnimatePresence>
            {isOpen && !isDisabled && (
                <motion.div
                    data-testid="chat-tool-menu"
                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 4, scale: 0.98 }}
                    transition={reduceMotion ? { duration: 0 } : { duration: 0.16, ease: EASE_OUT }}
                    style={{ transformOrigin: 'bottom left' }}
                    className="absolute bottom-full left-0 z-[1000] mb-0.5 max-h-[240px] min-w-[200px] max-w-[340px] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
                >
                    <button
                        type="button"
                        data-testid="chat-tool-option-none"
                        aria-pressed={!selectedToolId}
                        className={rowClass(!selectedToolId)}
                        style={rowStyle(!selectedToolId)}
                        onClick={() => {
                            onToolChange('');
                            setIsOpen(false);
                        }}
                    >
                        <span>{t('chatExchange.toolSelection.none')}</span>
                        {!selectedToolId && <Check className="h-4 w-4 shrink-0" aria-hidden />}
                    </button>
                    {tools.map((tool) => (
                        <button
                            key={tool.id}
                            type="button"
                            data-testid="chat-tool-option"
                            aria-pressed={selectedToolId === tool.id}
                            className={rowClass(selectedToolId === tool.id)}
                            style={rowStyle(selectedToolId === tool.id)}
                            onClick={() => {
                                onToolChange(tool.id);
                                setIsOpen(false);
                            }}
                        >
                            <span className="min-w-0 flex-1 overflow-hidden">
                                <span className="block truncate" title={tool.name}>
                                    {tool.name}
                                </span>
                                {tool.description && (
                                    <span
                                        className="block truncate text-xs text-muted-foreground"
                                        title={tool.description}
                                    >
                                        {tool.description}
                                    </span>
                                )}
                            </span>
                            {selectedToolId === tool.id && (
                                <Check className="h-4 w-4 shrink-0" aria-hidden />
                            )}
                        </button>
                    ))}
                </motion.div>
            )}
            </AnimatePresence>
        </div>
    );
};
